import { Injectable } from '@angular/core';

import { AvTelemetryEnvelope } from '../models/av-telemetry.model';
import { AvTelemetryLogService } from './av-telemetry-log.service';
import { collapseTicks } from './av-telemetry-tick-collapse';

/**
 * TEMPORARY. Exists only to format the interim Kusto message — see AvTelemetryLogService for
 * the full removal set. Does not drain; it consumes AvTelemetryBufferService.flushed$.
 *
 * Metrics ride as JSON at the END of the message rather than as structured properties, because
 * per-metric names are vendor-defined and would create unbounded log fields. Kusto recovers
 * them with `parse_json`, so nothing may be appended after the payload.
 *
 * Aggregation reuses collapseTicks, which keeps per-stream structure. Flattening by leaf name
 * would merge every video feed's metrics into one range, so a single failing stream would be
 * indistinguishable from every stream fluctuating.
 */
@Injectable({ providedIn: 'root' })
export class AvTelemetryReporterService {
  static readonly REPORT_INTERVAL_MS = 60_000;

  static readonly SUMMARY_MESSAGE_PREFIX = 'AvTelemetrySummary';
  static readonly DISRUPTION_MESSAGE_PREFIX = 'AvTelemetryEvent';
  static readonly META_MESSAGE_PREFIX = 'AvTelemetryMeta';

  static readonly PAYLOAD_SEPARATOR = ' | ';

  private droppedTotal = 0;

  constructor(private readonly avTelemetryLogService: AvTelemetryLogService) {}

  private get intervalLabel(): string {
    return `${AvTelemetryReporterService.REPORT_INTERVAL_MS / 60_000}min`;
  }

  /** Session-cumulative, supplied by the caller since the reporter no longer holds the sampler. */
  setDroppedTotal(droppedTotal: number): void {
    this.droppedTotal = droppedTotal;
  }

  report(records: readonly AvTelemetryEnvelope[]): void {
    if (!records.length) {
      return;
    }

    const ticks = records.filter((record) => record.type === 'tick');
    const events = records.filter((record) => record.type === 'event');
    const metas = records.filter((record) => record.type === 'meta');

    this.reportSummary(records, ticks, events.length);
    events.forEach((event) => this.reportDisruption(event));
    metas.forEach((meta) => this.reportMeta(meta));
  }

  /**
   * Once per session, at the first flush. Carries the client context the summary omits, and
   * `attendeeId` — without which a downstream stream key in a summary cannot be mapped back to
   * a device or participant. Already durable in the buffer; this only surfaces it in the log.
   */
  private reportMeta(meta: AvTelemetryEnvelope): void {
    const payload = meta.payload as { attendeeId?: string };

    this.emit(
      AvTelemetryReporterService.META_MESSAGE_PREFIX,
      `${meta.vendor} session context, attendee ${payload?.attendeeId ?? 'unknown'}`,
      {
        schemaVersion: meta.schemaVersion,
        vendor: meta.vendor,
        vendorVersion: meta.vendorVersion,
        seq: meta.seq,
        occurredAt: meta.occurredAt,
        ...(meta.payload as Record<string, unknown>),
      }
    );
  }

  private reportSummary(
    records: readonly AvTelemetryEnvelope[],
    ticks: readonly AvTelemetryEnvelope[],
    eventCount: number
  ): void {
    const spine = records[0];
    // One window over the whole batch, so an interval yields a single line. The window size is
    // dropped from the payload because `interval` already says it.
    const [aggregate] = collapseTicks(ticks, Number.MAX_SAFE_INTEGER);
    const {
      aggregateWindowMs,
      tickCount: aggregatedTickCount,
      ...aggregated
    } = (aggregate?.payload ?? {}) as { aggregateWindowMs?: number; tickCount?: number };
    // Always present and numeric, so a query never has to cope with a missing field.
    const tickCount = aggregatedTickCount ?? 0;

    this.emit(
      AvTelemetryReporterService.SUMMARY_MESSAGE_PREFIX,
      `${spine.vendor} ${this.intervalLabel} aggregate, ${tickCount} ticks, ${eventCount} events`,
      {
        schemaVersion: spine.schemaVersion,
        vendor: spine.vendor,
        vendorVersion: spine.vendorVersion,
        interval: this.intervalLabel,
        tickCount: tickCount,
        eventCount: eventCount,
        // Session-cumulative, not per-interval. Aggregate with max(), never sum().
        droppedTotal: this.droppedTotal,
        ...aggregated,
      }
    );
  }

  private reportDisruption(event: AvTelemetryEnvelope): void {
    const payload = event.payload as {
      name?: string;
      attributes?: Record<string, unknown>;
    };
    const eventName = payload?.name ?? 'unknown';

    this.emit(
      AvTelemetryReporterService.DISRUPTION_MESSAGE_PREFIX,
      `${event.vendor} ${eventName}`,
      {
        schemaVersion: event.schemaVersion,
        vendor: event.vendor,
        vendorVersion: event.vendorVersion,
        eventName: eventName,
        seq: event.seq,
        occurredAt: event.occurredAt,
        attributes: payload?.attributes ?? {},
      }
    );
  }

  /** Payload stays last so a Kusto `extract` can anchor on the trailing brace. */
  private emit(prefix: string, headline: string, payload: unknown): void {
    const message = [
      `${prefix}: ${headline}`,
      JSON.stringify(payload),
    ].join(AvTelemetryReporterService.PAYLOAD_SEPARATOR);

    this.avTelemetryLogService.send(message).subscribe({ error: () => undefined });
  }
}
